import React, { useRef, useEffect, useState, useCallback } from 'react'
import { stackEffects } from '../../utils/videoEffects'

/**
 * VideoPreview Component
 * 
 * Handles video preview with proper timeline synchronization:
 * - Shows correct frame based on timeline seeker position
 * - Considers start/end time and trim properties
 * - Respects layer properties (z-index)
 * - Simple, clean implementation without excessive nesting
 */
const VideoPreview = ({ 
  item, 
  previewTime, 
  isPlaying, 
  isVideoTemplate, 
  onTimeUpdate,
  maskMode = false,
  getAdjustmentLayersForItem = null // Function to get adjustment layers affecting this item
}) => {
  const videoRef = useRef(null)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [lastSeekTime, setLastSeekTime] = useState(0)

  // Calculate video timing properties
  const getVideoTiming = useCallback(() => {
    const itemStart = item.time?.start || 0
    const itemDuration = item.time?.duration || 0
    const itemEnd = itemStart + itemDuration
    
    // Trim properties
    const trimStart = item.trim?.start || 0
    const trimEnd = item.trim?.end || itemDuration
    
    return {
      itemStart,
      itemDuration, 
      itemEnd,
      trimStart,
      trimEnd
    }
  }, [item])

  // Calculate current video time based on timeline position
  const calculateVideoTime = useCallback(() => {
    const { itemStart, trimStart, trimEnd } = getVideoTiming()
    
    // Current time relative to item start
    const relativeTime = Math.max(0, previewTime - itemStart)
    
    // Apply trim offset and clamp to trim range
    const videoTime = Math.max(trimStart, Math.min(trimEnd, trimStart + relativeTime))
    
    return videoTime
  }, [previewTime, getVideoTiming])

  // Check if video should be visible at current time
  const isVideoVisible = useCallback(() => {
    const { itemStart, itemEnd } = getVideoTiming()
    return previewTime >= itemStart && previewTime <= itemEnd
  }, [previewTime, getVideoTiming])

  // Seek video to correct time when timeline changes
  useEffect(() => {
    if (!videoRef.current || !isVideoReady || (!isVideoTemplate && !maskMode)) return

    const videoEl = videoRef.current
    const targetTime = calculateVideoTime()
    
    // Only seek if time difference is significant to avoid constant seeking
    if (Math.abs(videoEl.currentTime - targetTime) > 0.1) {
      videoEl.currentTime = targetTime
      setLastSeekTime(targetTime)
    }
  }, [previewTime, isVideoReady, isVideoTemplate, calculateVideoTime, maskMode])

  // Handle video loaded
  const handleVideoLoaded = useCallback(() => {
    setIsVideoReady(true)
    
    if ((isVideoTemplate || maskMode) && videoRef.current) {
      const targetTime = calculateVideoTime()
      videoRef.current.currentTime = targetTime
      setLastSeekTime(targetTime)
    }
  }, [isVideoTemplate, calculateVideoTime, maskMode])

  // Handle video time update during playback
  const handleTimeUpdate = useCallback((e) => {
    if (!isPlaying || !isVideoTemplate || maskMode || !onTimeUpdate) return
    
    const videoEl = e.target
    const { itemStart } = getVideoTiming()
    
    // Calculate timeline time from video time
    const timelineTime = itemStart + (videoEl.currentTime - getVideoTiming().trimStart)
    
    // Update timeline if video time has changed significantly
    if (Math.abs(timelineTime - previewTime) > 0.1) {
      onTimeUpdate(timelineTime)
    }
  }, [isPlaying, isVideoTemplate, previewTime, onTimeUpdate, getVideoTiming, maskMode])

  // Generate video styles with effects applied
  const getVideoStyles = useCallback(() => {
    // Get item effects
    const itemEffects = item.effects || []
    
    // Get adjustment layer effects if function is provided
    let adjustmentLayerEffects = []
    if (getAdjustmentLayersForItem && typeof getAdjustmentLayersForItem === 'function') {
      const adjustmentLayers = getAdjustmentLayersForItem(item)
      adjustmentLayers.forEach(adjLayer => {
        if (adjLayer.effects && Array.isArray(adjLayer.effects)) {
          const adjStart = adjLayer.time?.start || 0
          adjLayer.effects.forEach(effect => {
            adjustmentLayerEffects.push({
              ...effect,
              start: (effect.start || 0) + adjStart
            })
          })
        }
      })
    }
    
    // Combine all effects
    const allEffects = adjustmentLayerEffects.concat(itemEffects)
    
    // Calculate current time relative to item start
    const itemStart = item.time?.start || 0
    const currentTime = previewTime || 0
    const relativeTime = currentTime - itemStart
    
    // Stack effects and get combined CSS
    const effectStyles = stackEffects(allEffects, relativeTime)
    
    // Base styles
    const baseFilter = (() => {
      // Use filters.cssFilter if available, otherwise build from individual properties
      if (item.filters?.cssFilter) {
        return item.filters.cssFilter
      }
      
      return `
        brightness(${(item.brightness ?? 0) + 100}%)
        contrast(${(item.contrast ?? 0) + 100}%)
        saturate(${(item.intensity ?? 0) + 100}%)
        hue-rotate(${item.hueRotate || 0}deg)
        sepia(${Math.max(0, item.sepia || 0)}%)
        grayscale(${Math.max(0, item.grayscale || 0)}%)
        blur(${Math.max(0, item.blur || 0)}px)
      `.trim()
    })()
    
    // Combine base filter with effect filters
    const combinedFilter = [baseFilter, effectStyles.filter].filter(Boolean).join(' ')
    
    // Base transform
    const baseTransform = `scaleX(${item.flipX ? -1 : 1}) scaleY(${item.flipY ? -1 : 1})`
    
    // Combine base transform with effect transforms
    const combinedTransform = [baseTransform, effectStyles.transform].filter(Boolean).join(' ')
    
    // Combine opacity
    const combinedOpacity = (item.opacity ?? 1) * effectStyles.opacity
    
    return {
      opacity: combinedOpacity,
      filter: combinedFilter || 'none',
      transform: combinedTransform || 'none',
      transformOrigin: effectStyles.transformOrigin || 'center center',
      WebkitMaskImage: item.gradientMask || undefined,
      maskImage: item.gradientMask || undefined,
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      mixBlendMode: item.blendMode || 'normal',
      boxShadow: getBoxShadow()
    }
  }, [item, previewTime, getAdjustmentLayersForItem])

  // Generate box shadow
  const getBoxShadow = useCallback(() => {
    if (!item.shadow?.enabled) {
      return item.boxShadow || 'none'
    }

    const { offsetX, offsetY, blur, spread, color, opacity, inset, gradient } = item.shadow
    const shadowColor = gradient?.stops?.[0]?.color || color || '#000000'
    const shadowOpacity = opacity || 0.25
    
    const rgbaColor = shadowColor.startsWith('#')
      ? `rgba(${parseInt(shadowColor.slice(1, 3), 16)}, ${parseInt(shadowColor.slice(3, 5), 16)}, ${parseInt(shadowColor.slice(5, 7), 16)}, ${shadowOpacity})`
      : shadowColor

    return `${inset ? 'inset ' : ''}${offsetX || 4}px ${offsetY || 4}px ${blur || 8}px ${spread || 0}px ${rgbaColor}`
  }, [item.shadow, item.boxShadow])

  // Don't render if video is not visible at current time
  const visible = isVideoVisible()
  if (!visible) {
    if (!maskMode) {
      return null
    }
    return null
  }

  return (
    <div className="w-full h-full" style={getVideoStyles()}>
      <video
        ref={videoRef}
        src={item.src || item.file?.url}
        className="w-full h-full object-fill rounded"
        draggable={false}
        muted={true} // Always mute video elements - audio is handled by Web Audio API mixer
        loop={item.loop || false}
        autoPlay={false} // Never autoplay in editor
        playsInline={true}
        preload="metadata"
        onLoadedData={handleVideoLoaded}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleVideoLoaded}
        style={{
          display: visible ? 'block' : 'none'
        }}
      />
    </div>
  )
}

export default VideoPreview
